import React, { useState, useEffect, useRef } from "react";
import MultiSelectionDropdown from "../../components/component/MultiSelectionDropdown";

const FortigatePortVlanTable = ({
  typeList,
  data,
  handleSelectMemberType,
  handleAccessEnable,
  handleSelectAccessMember,
  getHybridMembers,
  dropdownValues,
  hasConflicts,
  communicationType,
  secureData,
  vlanConfigurationType,
  wanSelected,
  vlanSwitchTakenAccessMembers,
  accessVlansInConflict,
}) => {
  const newData = { ...data };

  // Determine if the current port is a WAN port; if so, we need to remove VLAN_SWITCH from the list
  const isWanPort = ["WAN1", "WAN2"].includes(newData.port);
  let effectiveTypeList = typeList || [];

  if (isWanPort) {
    // For WAN ports remove VLAN_SWITCH
    effectiveTypeList = effectiveTypeList.filter(t => t !== "VLAN_SWITCH");
  }

  // If incoming data has memberType VLAN_SWITCH on a WAN port, fallback to first allowed option
  let initialMemberType = newData.memberType;
  if (!effectiveTypeList.includes(initialMemberType)) {
    initialMemberType = effectiveTypeList[0] || "";
  }

  const [selectedMemberType, setSelectedMemberType] = useState({
    memberType: initialMemberType,
    port: newData.port,
  });
  const [accessEnable, setAccessEnable] = useState(newData.enable);
  const [selectedAccessMember, setSelectedAccessMember] = useState(
    newData?.accessMembers?.vlan
  );
  const [selectedTrunkMember, setSelectedTrunkMember] = useState(newData);

  const getTrunkMembers = (members, port) => {
    // Persist locally so dropdown reflects chosen values
    setSelectedTrunkMember(prev => ({
      ...prev,
      hybridMembers: Array.isArray(members) ? members : [],
    }));
    if (typeof dropdownValues === 'function') {
      dropdownValues(members, port, communicationType);
    } else {
      console.warn('dropdownValues prop is missing or not a function in FortigatePortVlanTable');
    }
  };

  const isFirstVlanSwitchMgmt = (() => {
    if (!secureData || !communicationType) return false;
    const list = Array.isArray(secureData[communicationType]) ? secureData[communicationType] : [];
    let firstPort = null;
    for (let i = 0; i < list.length; i++) {
      const row = list[i];
      if (row?.memberType === 'VLAN_SWITCH' && row?.accessMembers?.vlan === 'MGMT') {
        firstPort = row.port;
        break;
      }
    }
    return firstPort !== null && firstPort === newData.port;
  })();

  // Auto-select all trunk members by default for the first VLAN_SWITCH + MGMT port
  const hasAutoSelected = useRef(false);
  useEffect(() => {
    if (
      selectedMemberType.memberType === 'VLAN_SWITCH' &&
      isFirstVlanSwitchMgmt &&
      (!data.hybridMembers || data.hybridMembers.length === 0) &&
      !hasAutoSelected.current
    ) {
      const allTrunkOptions = (secureData?.securedCommunicationArchitecture || [])
        .filter(item => ["Control", "Breakers", "Meters", "Relays", "EMA_WAN"].includes(item.vlan))
        .map(network => ({
          ...network,
          label: network.vlan,
          value: network.vlan,
        }));

      if (allTrunkOptions.length > 0) {
        hasAutoSelected.current = true;
        getTrunkMembers(allTrunkOptions, selectedMemberType);
      }
    }
  }, [isFirstVlanSwitchMgmt, selectedMemberType.memberType]);

  // Reset auto-select flag when memberType changes away from VLAN_SWITCH
  useEffect(() => {
    if (selectedMemberType.memberType !== 'VLAN_SWITCH') {
      hasAutoSelected.current = false;
    }
  }, [selectedMemberType.memberType]);

  // Check if any access member VLANs (from any port) conflict with this port's trunk members
  const trunkMemberVlans = (selectedTrunkMember?.hybridMembers || [])?.map(m => m?.vlan);
  // Cross-row conflict: VLANs that are both in trunk members AND used as access members by other ports
  const conflictVlansForDropdown = (accessVlansInConflict || []).filter(v => trunkMemberVlans.includes(v));
  const hasLocalConflict = conflictVlansForDropdown.length > 0;

  return (
    <tr className="text-center">
      <td width={"8%"}>{newData.port}</td>
      <td width={"8%"}>
        <div className="custom-control custom-checkbox">
          <input
            type="checkbox"
            className="custom-control-input asCompatible"
            name="vlanOptions"
            id={`fortigatePortVlanOptions-${newData.id}`}
            value={accessEnable}
            checked={accessEnable}
            onChange={(e) => {
              setAccessEnable(e.target.checked);
              handleAccessEnable(e.target.checked, newData.port, communicationType);
            }}
          />
          <label
            className="custom-control-label line-height-25"
            htmlFor={`fortigatePortVlanOptions-${newData.id}`}
          />
        </div>
      </td>
      <td width={"10%"}>
        <select
          className="col-sm-12 col-lg-12 col-md-12 col-xl-12 form-control form-control-sm"
          name="memberType"
          value={selectedMemberType.memberType}
          onChange={(event) => {
            let member = event.target.value;
            handleSelectMemberType(member, newData.port, communicationType);

            // If VLAN_SWITCH selected, default accessMember to MGMT immediately
            if (member === 'VLAN_SWITCH') {
              const mgmtVlan = (secureData?.securedCommunicationArchitecture || []).find(v => v.vlan === 'MGMT');
              if (mgmtVlan) {
                setSelectedAccessMember('MGMT');
              }
            } else if (member === 'WAN') {
              // For WAN interface type, force Network option to GTWY
              const gtwyVlan = (secureData?.securedCommunicationArchitecture || []).find(v => v.vlan === 'GTWY');
              if (gtwyVlan) {
                setSelectedAccessMember('GTWY');
              }
            }

            setSelectedMemberType((prev) => ({
              ...prev,
              memberType: member,
              port: newData.port
            }));
          }}
        >
          {effectiveTypeList?.map(type => (
            <option
              value={type}
              key={type}
              disabled={type === 'WAN' && wanSelected && selectedMemberType.memberType !== 'WAN'}
            >
              {type}
            </option>
          ))}
        </select>
      </td>
      <td width={"12%"}>
        <select
          className={`"col-sm-12 col-lg-12 col-md-12 col-xl-12 form-control form-control-sm " ${(hasConflicts?.includes(selectedAccessMember) || hasLocalConflict) && selectedAccessMember!== 'MGMT' ? "is-mandatory" :""}`}
          name="accessMember"
          value={selectedAccessMember}
          onChange={(event) => {
            handleSelectAccessMember(event, newData.port, communicationType);
            setSelectedAccessMember(event.target.value);
          }}

        >
          {secureData?.securedCommunicationArchitecture?.map((data) => (
            <option
              key={data.id}
              value={data.vlan}
              disabled={
                selectedMemberType.memberType === 'VLAN_SWITCH'
                  ? data.vlan !== 'MGMT'
                  : selectedMemberType.memberType === 'WAN'
                    ? data.vlan !== 'GTWY'
                    : (Array.isArray(vlanSwitchTakenAccessMembers) && vlanSwitchTakenAccessMembers.includes(data.vlan) && selectedAccessMember !== data.vlan)
              }
            >
              {data.vlan}
            </option>
          ))}
        </select>
      </td>
      <td width={"12%"} className="text-center">
        {["HYBRID_TRUNK", "TRUNK"].includes(selectedMemberType.memberType) && (
          <MultiSelectionDropdown
            secureData={secureData}
            trunkMembers={selectedTrunkMember}
            setSelectedTrunkMember={setSelectedTrunkMember}
            getHybridMembers={getHybridMembers}
            getTrunkMembers={getTrunkMembers}
            vlanConfigurationType={vlanConfigurationType}
            selectedMemberType={selectedMemberType}
            hasConflict={hasLocalConflict}
            conflictVlans={conflictVlansForDropdown}
          />
        )}
        {["VLAN_SWITCH"].includes(selectedMemberType.memberType) && isFirstVlanSwitchMgmt && (
          <MultiSelectionDropdown
            secureData={secureData}
            trunkMembers={data}
            setSelectedTrunkMember={setSelectedTrunkMember}
            getHybridMembers={getHybridMembers}
            getTrunkMembers={getTrunkMembers}
            vlanConfigurationType={vlanConfigurationType}
            selectedMemberType={selectedMemberType}
            hasConflict={hasLocalConflict}
            conflictVlans={conflictVlansForDropdown}
          />
        )}
      </td>
    </tr>
  );
};

export default FortigatePortVlanTable;